import { z } from 'zod'

const AdkPartSchema = z.object({
  text: z.string().optional(),
  function_call: z.object({ name: z.string() }).optional(),
  function_response: z.object({ name: z.string() }).optional(),
})

// Custom envelope events emitted by the Mesh proxy / backend.
// All share a `type` discriminant forming a proper discriminated union.
//
// Unknown `type` values: parseStreamEvent returns null and the event is silently
// skipped. The UI stays functional — unrecognised events are ignored rather than
// crashing. Log a warning in dev if you need visibility into new backend types.

const SessionCreatedEventSchema = z.object({
  type: z.literal('session_created'),
  session_id: z.string(),
})

// Emitted for both streaming text deltas and single-shot messages (e.g. cancel confirmation).
const TextDeltaEventSchema = z.object({
  type: z.literal('text_delta'),
  text: z.string(),
  final: z.boolean().optional(),
})

// Emitted when a tool call completes (e.g. write_op_resumed after approval).
const ToolCompletedEventSchema = z.object({
  type: z.literal('tool_completed'),
  tool_name: z.string(),
  result: z.record(z.unknown()).optional(),
})

const ApprovalRequiredEventSchema = z.object({
  type: z.literal('approval_required'),
  suspension_token: z.string(),
  tool_name: z.string().optional(),
  preview: z.object({ summary: z.string() }).optional(),
})

const ProviderErrorEventSchema = z.object({
  type: z.literal('provider_error'),
  message: z.string().optional(),
})

export const TypedEventSchema = z.discriminatedUnion('type', [
  SessionCreatedEventSchema,
  TextDeltaEventSchema,
  ToolCompletedEventSchema,
  ApprovalRequiredEventSchema,
  ProviderErrorEventSchema,
])

// ADK native streaming events — no `type` field; identified by `partial` + `content`.
export const AdkEventSchema = z.object({
  partial: z.boolean(),
  content: z
    .object({
      parts: z.array(AdkPartSchema).optional(),
      role: z.string().optional(),
    })
    .optional(),
})

export type TypedEvent = z.infer<typeof TypedEventSchema>
export type AdkEvent = z.infer<typeof AdkEventSchema>
export type StreamEvent = TypedEvent | AdkEvent

export function parseStreamEvent(json: unknown): StreamEvent | null {
  // Custom events carry a `type` field — parse with the discriminated union.
  if (typeof json === 'object' && json !== null && 'type' in json) {
    const result = TypedEventSchema.safeParse(json)
    if (!result.success) {
      // Surfaces unrecognised/malformed typed events — add to TypedEventSchema if this is a new backend type.
      console.warn(
        '[retailer-agent] typed SSE event failed schema parse:',
        json,
        result.error.format()
      )
      return null
    }
    return result.data
  }
  // ADK native events carry `partial` + optional `content`.
  const result = AdkEventSchema.safeParse(json)
  if (!result.success) {
    console.warn('[retailer-agent] ADK event failed schema parse:', json, result.error.format())
    return null
  }
  return result.data
}
